import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { getAdjustedStartDate, getAdjustedEndDate } from '../../utils/dateHelper';
import ReactPaginate from 'react-paginate';

const LicenseDashboard = () => {
    const navigate = useNavigate();
    const [data, setData] = useState({ summary: {}, details: [] });
    const [isLoading, setIsLoading] = useState(false);
    const [activeBucket, setActiveBucket] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 15;

    const getFinancialYear = () => {
        const now = new Date();
        return { startDate: `${now.getFullYear()}-01-01`, endDate: `${now.getFullYear()}-12-31` };
    };

    const [selectedFilter, setSelectedFilter] = useState('financialYear');
    const [isDateFilterVisible, setIsDateFilterVisible] = useState(false);
    const [customDateRange, setCustomDateRange] = useState({ startDate: '', endDate: '' });
    const [dashboardStartDate, setDashboardStartDate] = useState(() => getFinancialYear().startDate);
    const [dashboardEndDate, setDashboardEndDate] = useState(() => getFinancialYear().endDate);

    const fetchLicenseDashboard = async (sDate, eDate) => {
        setIsLoading(true);
        const finalStart = sDate || dashboardStartDate;
        const finalEnd = eDate || dashboardEndDate;
        if (sDate || eDate) {
            setDashboardStartDate(finalStart);
            setDashboardEndDate(finalEnd);
        }
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            const response = await axios.get(`${backendUrl}/api/dashboard/license`, {
                params: { start_date: finalStart, end_date: finalEnd },
                headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin },
            });
            setData(response.data);
        } catch (error) {
            console.error('Error fetching license dashboard', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { fetchLicenseDashboard(); }, []);
    useEffect(() => { setCurrentPage(0); }, [activeBucket, data]);

    const handleFinancialYearChange = (selectedKey) => {
        if (selectedKey === 'financialYear') {
            setSelectedFilter('financialYear');
            setIsDateFilterVisible(false);
            const { startDate, endDate } = getFinancialYear();
            fetchLicenseDashboard(startDate, endDate);
        } else {
            setSelectedFilter('customRange');
            setIsDateFilterVisible(true);
            setCustomDateRange({ startDate: '', endDate: '' });
        }
    };

    const handleClearDates = () => {
        setCustomDateRange({ startDate: '', endDate: '' });
        setIsDateFilterVisible(false);
        const { startDate, endDate } = getFinancialYear();
        fetchLicenseDashboard(startDate, endDate);
    };

    const handleSubmit = () => {
        const { startDate, endDate } = customDateRange;
        if (startDate && endDate) {
            fetchLicenseDashboard(startDate, endDate);
            setIsDateFilterVisible(false);
        }
    };

    const { summary = {}, details = [] } = data;
    const filteredDetails = activeBucket === 'all' ? details : details.filter((d) => d.bucket === activeBucket);
    const pageCount = Math.ceil(filteredDetails.length / itemsPerPage) || 1;
    const paginatedDetails = filteredDetails.slice(currentPage * itemsPerPage, currentPage * itemsPerPage + itemsPerPage);

    // key: summary field, icon: bx icon class, color: accent, tint: light bg for the icon badge
    const bucketMeta = [
        { key: 'active', label: 'Active', icon: 'bx-check-shield', color: '#198754', tint: '#e8f5ee' },
        { key: 'expired', label: 'Expired', icon: 'bx-error-circle', color: '#dc3545', tint: '#fdeaec' },
        { key: '1_day', label: 'Expiring in 1 Day', icon: 'bx-alarm-exclamation', color: '#fd7e14', tint: '#fef1e6' },
        { key: '7_days', label: 'Expiring in 7 Days', icon: 'bx-time-five', color: '#e0a800', tint: '#fdf5e0' },
        { key: '30_days', label: 'Expiring in 30 Days', icon: 'bx-calendar', color: '#0d8ecf', tint: '#e5f4fb' },
    ];

    const getCount = (key) => summary[key === 'active' || key === 'expired' ? key : `expiring_${key}`] || 0;
    const total = summary.total || 0;

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row w-100">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <div>
                                    <h4 className="mb-0 font-size-18">License Management</h4>
                                    <p className="text-muted mb-0 font-size-13">
                                        {total} license{total === 1 ? '' : 's'} tracked across all setups
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-end align-items-center mb-3">
                        <DropdownButton
                            id="license-financial-year-dropdown"
                            title={selectedFilter === 'financialYear' ? 'This Financial Year' : 'Custom Range'}
                            onSelect={handleFinancialYearChange}
                            variant="outline-primary"
                            className="filter-dropdown"
                        >
                            <Dropdown.Item eventKey="financialYear">This Financial Year</Dropdown.Item>
                            <Dropdown.Item eventKey="custom">Custom Range</Dropdown.Item>
                        </DropdownButton>

                        {isDateFilterVisible && (
                            <div className="date-range-selector d-flex align-items-center gap-2 ms-2">
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        views={['year', 'month']}
                                        openTo="year"
                                        label="Start Month"
                                        format="MMM YYYY"
                                        value={customDateRange.startDate ? dayjs(customDateRange.startDate, "YYYY-MM-DD") : null}
                                        onChange={(newValue) => {
                                            if (newValue) {
                                                const currentStart = customDateRange.startDate ? dayjs(customDateRange.startDate, "YYYY-MM-DD") : null;
                                                if (!currentStart || currentStart.year() !== newValue.year()) {
                                                    setCustomDateRange(prev => ({
                                                        ...prev,
                                                        startDate: newValue.startOf('year').format('YYYY-MM-DD'),
                                                        endDate: newValue.endOf('year').format('YYYY-MM-DD')
                                                    }));
                                                } else {
                                                    setCustomDateRange(prev => ({
                                                        ...prev,
                                                        startDate: newValue.startOf('month').format('YYYY-MM-DD')
                                                    }));
                                                }
                                            } else {
                                                setCustomDateRange(prev => ({ ...prev, startDate: '' }));
                                            }
                                        }}
                                        slotProps={{ textField: { size: 'small', className: "form-feild", sx: { width: '140px', background: 'white' } } }}
                                    />
                                </LocalizationProvider>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        views={['year', 'month']}
                                        openTo="year"
                                        label="End Month"
                                        format="MMM YYYY"
                                        value={customDateRange.endDate ? dayjs(customDateRange.endDate, "YYYY-MM-DD") : null}
                                        onChange={(newValue) => {
                                            if (newValue) {
                                                const currentEnd = customDateRange.endDate ? dayjs(customDateRange.endDate, "YYYY-MM-DD") : null;
                                                if (!currentEnd || currentEnd.year() !== newValue.year()) {
                                                    setCustomDateRange(prev => ({
                                                        ...prev,
                                                        startDate: newValue.startOf('year').format('YYYY-MM-DD'),
                                                        endDate: newValue.endOf('year').format('YYYY-MM-DD')
                                                    }));
                                                } else {
                                                    setCustomDateRange(prev => ({
                                                        ...prev,
                                                        endDate: newValue.endOf('month').format('YYYY-MM-DD')
                                                    }));
                                                }
                                            } else {
                                                setCustomDateRange(prev => ({ ...prev, endDate: '' }));
                                            }
                                        }}
                                        slotProps={{ textField: { size: 'small', className: "form-feild", sx: { width: '140px', background: 'white' } } }}
                                    />
                                </LocalizationProvider>
                                <button onClick={handleClearDates} className="btn btn-outline-secondary">Clear</button>
                                <button onClick={handleSubmit} className="btn btn-primary" disabled={!customDateRange.startDate || !customDateRange.endDate}>Apply</button>
                            </div>
                        )}
                    </div>

                    {isLoading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading…</span>
                            </div>
                        </div>
                    ) : (
                        <>


                            {/* Summary cards */}
                            <div className="row g-3 mb-4">
                                <div className="col-lg-2 col-md-4 col-6">
                                    <div
                                        className="card shadow-sm border-0 h-100"
                                        style={{ borderRadius: '15px', cursor: 'pointer', outline: activeBucket === 'all' ? '2px solid #6c757d' : 'none' }}
                                        onClick={() => setActiveBucket(activeBucket === 'all' ? null : 'all')}
                                    >
                                        <div className="card-body">
                                            <h6 className="text-muted mb-1 font-size-13">All Licenses</h6>
                                            <p className="fw-bold mb-0" style={{ fontSize: '28px', lineHeight: 1 }}>{total}</p>
                                        </div>
                                    </div>
                                </div>

                                {bucketMeta.map((b) => (
                                    <div className="col-lg-2 col-md-4 col-6" key={b.key}>
                                        <div
                                            className="card shadow-sm border-0 h-100"
                                            style={{ borderRadius: '15px', cursor: 'pointer', outline: activeBucket === b.key ? `2px solid ${b.color}` : 'none' }}
                                            onClick={() => setActiveBucket(activeBucket === b.key ? null : b.key)}
                                        >
                                            <div className="card-body">
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <span
                                                        className="d-flex align-items-center justify-content-center"
                                                        style={{ width: 32, height: 32, borderRadius: '9px', backgroundColor: b.tint }}
                                                    >
                                                        <i className={`bx ${b.icon}`} style={{ color: b.color, fontSize: '16px' }}></i>
                                                    </span>
                                                </div>
                                                <h6 className="text-muted mb-1 font-size-13">{b.label}</h6>
                                                <p className="fw-bold mb-0" style={{ fontSize: '28px', lineHeight: 1, color: b.color }}>
                                                    {getCount(b.key)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Detail table */}
                            <div className="row">
                                <div className="col-12">
                                    <div className="card shadow-sm border-0" style={{ borderRadius: '15px' }}>
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h5 className="fw-bold mb-0">
                                                    {!activeBucket ? 'Select a Category' : activeBucket === 'all' ? 'All Licenses' : bucketMeta.find((b) => b.key === activeBucket)?.label}
                                                </h5>
                                                {activeBucket && (
                                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setActiveBucket(null)}>
                                                        Close Table
                                                    </button>
                                                )}
                                            </div>

                                            <div className="table-responsive">
                                                <table className="table align-middle table-nowrap table-check table-list">
                      <thead className="table-sales-gradient">
                                                        <tr>
                                                            {/* <th>Setup ID</th> */}
                                                            <th>Client Name</th>
                                                            {/* <th>Key</th> */}
                                                            <th>Tenant ID</th>
                                                            <th>Validity</th>
                                                            <th>Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {!activeBucket ? (
                                                            <tr>
                                                                <td colSpan={6} className="text-center text-muted py-4">Click a card above to view licenses</td>
                                                            </tr>
                                                        ) : paginatedDetails.length === 0 ? (
                                                            <tr>
                                                                <td colSpan={6} className="text-center text-muted py-4">No records found</td>
                                                            </tr>
                                                        ) : (
                                                            paginatedDetails.map((row, idx) => {
                                                                const meta = bucketMeta.find((b) => b.key === row.bucket);
                                                                return (
                                                                    <tr
                                                                        key={row.license_id}
                                                                        style={{ cursor: 'pointer', backgroundColor: idx % 2 === 1 ? '#fafbfc' : 'transparent' }}
                                                                        onClick={() => navigate('/setup-info', { 
                                                                            state: { 
                                                                                searchKey: row.key, 
                                                                                autoOpenSetup: true, 
                                                                                autoOpenTab: 'licenseList', 
                                                                                activeExpiryFilter: row.bucket === 'all' ? '' : row.bucket,
                                                                                autoOpenLicenseId: row.license_id
                                                                            } 
                                                                        })}
                                                                    >
                                                                        {/* <td>{row.setup_id}</td> */}
                                                                        <td>{row.client_name}</td>
                                                                        {/* <td>{row.key}</td> */}
                                                                        <td>{row.tenant_id ?? ''}</td>
                                                                        <td>{row.validity ? dayjs(row.validity).format('DD-MM-YYYY') : ''}</td>
                                                                        <td>
                                                                            <span
                                                                                className="badge d-inline-flex align-items-center gap-1"
                                                                                style={{ backgroundColor: meta?.tint || '#f1f3f5', color: meta?.color || '#6c757d', fontWeight: 500 }}
                                                                            >
                                                                                {meta && <i className={`bx ${meta.icon}`} style={{ fontSize: '12px' }}></i>}
                                                                                {meta?.label || row.bucket}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {pageCount > 1 && (
                                                <div className="d-flex justify-content-end mt-3">
                                                    <ReactPaginate
                                                        previousLabel={"<"}
                                                        nextLabel={">"}
                                                        breakLabel={"..."}
                                                        pageCount={pageCount}
                                                        marginPagesDisplayed={2}
                                                        pageRangeDisplayed={3}
                                                        onPageChange={(d) => setCurrentPage(d.selected)}
                                                        forcePage={currentPage}
                                                        containerClassName={"pagination pagination-rounded"}
                                                        pageClassName={"page-item"}
                                                        pageLinkClassName={"page-link"}
                                                        previousClassName={"page-item"}
                                                        previousLinkClassName={"page-link"}
                                                        nextClassName={"page-item"}
                                                        nextLinkClassName={"page-link"}
                                                        breakClassName={"page-item"}
                                                        breakLinkClassName={"page-link"}
                                                        activeClassName={"active"}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LicenseDashboard;